import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import "./styles.css";

// fonts
import "@fontsource/barlow/300.css";
import "@fontsource/barlow/400.css";
import "@fontsource/barlow/500.css";
import "@fontsource/barlow/600.css";

// icons
import ElrinFullLogo from "./assets/elrin-full-logo.png";

// utils
import theme, { colorPalette } from "./theme";
import { useAxios } from "./hooks";

// components
import { ConfigProvider, Layout } from "antd";
import FadeTransition from "./components/FadeTransition";
import SignUp from "./components/SignUp";
import CompanyInfo from "./components/CompanyInfo";
import SurveyPreview from "./components/SurveyPreview";
import EmailTemplate from "./components/EmailTemplate";
import FullPageLoader from "./components/FullPageLoader";

const StyledLayout = styled(Layout)`
  // height: 100vh;
  padding: 0px;
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0px 40px 40px;
  background: linear-gradient(180deg, #a8cceb 0%, #d4e5f4 50%, #f7f5f5 100%);

  img {
    margin-top: 20px;
    height: 50px;
    width: auto;
  }
`;

function App() {
  const navigate = useNavigate();
  const { fetch, loading } = useAxios();
  const [currentStep, setCurrentStep] = useState(1);
  const [onboardingInfo, setOnboardingInfo] = useState(null);

  useEffect(() => {
    if (window.location.pathname.includes("/onboarding/")) {
      const onboardingId = window.location.pathname.split("/onboarding/")[1];
      fetchData(onboardingId);
    }
  }, [window.location.pathname]);

  const fetchData = async (onboardingId, nextStep) => {
    try {
      const { data } = await fetch({
        method: "GET",
        url: `/company/onboarding/${onboardingId}/`,
      });
      // console.log("onboarding", data);
      setOnboardingInfo({ ...data, step: Math.random() });
      setCurrentStep(nextStep || data.current_step);
    } catch {
      setCurrentStep(1);
      navigate("/");
    }
  };

  const component = useMemo(() => {
    switch (currentStep) {
      case 1:
        return <SignUp />;
      case 2:
        return (
          <CompanyInfo
            onboarding={onboardingInfo}
            onChangeStep={() => setCurrentStep(3)}
            onUpdateOnboarding={(data) => setOnboardingInfo(data)}
          />
        );
      case 3:
        return (
          <SurveyPreview
            onboarding={{ ...onboardingInfo }}
            onChangeStep={() => {
              fetchData(onboardingInfo?.id, 4);
            }}
            onPreviousStep={() => setCurrentStep(2)}
          />
        );
      case 4:
        return (
          <EmailTemplate
            onboarding={onboardingInfo}
            onChangeStep={() => setCurrentStep(5)}
            onPreviousStep={() => setCurrentStep(3)}
          />
        );
      // case 5:
      //   return (
      //     <Payment
      //       onboarding={onboardingInfo}
      //       onChangeStep={() => setCurrentStep(5)}
      //     />
      //   );
      default:
        return null;
    }
  }, [currentStep, onboardingInfo]);

  if (loading) {
    return <FullPageLoader />;
  }

  return (
    <ConfigProvider
      theme={{
        ...theme,
        token: {
          ...theme.token,
          colorPrimary: colorPalette.primary[500],
        },
      }}
    >
      <StyledLayout loading={loading}>
        <LogoWrapper>
          <img src={ElrinFullLogo} alt="logo" />
        </LogoWrapper>
        <FadeTransition step={currentStep}>{component}</FadeTransition>
      </StyledLayout>
    </ConfigProvider>
  );
}

export default App;
